import { useState, useEffect, useCallback } from "react";
import { getRouteApi } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Revision } from "../../Tabs/RevisionsTab";
import {
  AnomalyRun,
  Certification,
  CertificationLog,
} from "@/models/file-submission";
import { fetchAuthenticatedData } from "@/queries/shared";
import { GlobalStateModel } from "@/models";

const rootRoute = getRouteApi("__root__");

export async function postCertificationEdit(
  globalState: GlobalStateModel,
  esiidPath: string,
  cert: Certification
): Promise<void> {
  const accessToken = await globalState.msalTokenFn();
  const fetchUrl = new URL(
    `/json/certification/${esiidPath}/edit`,
    globalState.appUrl
  ).href;

  const certPayload: Omit<Certification, "AnomalyRun" | "AnomalyOverrides"> = {
    Id: cert.Id,
    ReportingLea: cert.ReportingLea,
    ReportingCalendarId: cert.ReportingCalendarId,
    Esiid: cert.Esiid,
    Submission: cert.Submission,
    AnomalyRunId: cert.AnomalyRunId,
    Status: cert.Status,
    Step: cert.Step,
    LastReviewer: cert.LastReviewer,
    LastReviewerDate: cert.LastReviewerDate,
    WindowStart: cert.WindowStart,
    WindowEnd: cert.WindowEnd,
    Deadline: cert.Deadline,
    AmendmentEnd: cert.AmendmentEnd,
    CertifierFirstName: cert.CertifierFirstName,
    CertifierLastName: cert.CertifierLastName,
    CertifierEmail: cert.CertifierEmail,
    CertifierPhoneNumber: cert.CertifierPhoneNumber,
    CertifierExtension: cert.CertifierExtension,
  };

  const res = await fetch(fetchUrl, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(certPayload),
  });

  if (res.status === 401) window.location.reload();
  if (res.status >= 400 && res.status < 500) {
    throw new Error(
      `Certification edit failed: ${res.status} ${res.statusText}`
    );
  }
  // Don't throw on 500 — the data saves, the response just fails to serialize.
}

type UseRevisionsParams = {
  esiid: string;
  esiidPath: string;
  certificationId: string;
  mostRecentRevision: Revision | null;
  lockedRevision: Revision | null;
  revisionHistory: Revision[];
  certificationStatus: "Uncertified" | "Certified" | "Decertified";
  isCycleEnded: boolean;
};

export default function useRevisions({
  esiid,
  esiidPath,
  certificationId,
  mostRecentRevision,
  lockedRevision: initialLockedRevision,
  revisionHistory,
  certificationStatus,
  isCycleEnded,
}: UseRevisionsParams) {
  const globalState = rootRoute.useLoaderData();
  const queryClient = useQueryClient();

  const [lockedRevisionId, setLockedRevisionId] = useState<string | null>(
    initialLockedRevision?.id ?? null
  );

  useEffect(() => {
    setLockedRevisionId(initialLockedRevision?.id ?? null);
  }, [initialLockedRevision?.id]);
  const [toast, setToast] = useState<{
    title: string;
    message: string;
    variant: "lock" | "unlock";
  } | null>(null);

  const lockedRevision = (() => {
    if (!lockedRevisionId) return null;
    if (lockedRevisionId === mostRecentRevision?.id) return mostRecentRevision;
    if (lockedRevisionId === initialLockedRevision?.id)
      return initialLockedRevision;
    return revisionHistory.find((r) => r.id === lockedRevisionId) ?? null;
  })();

  const isCertified = certificationStatus === "Certified";
  const isLocked = !!lockedRevision;
  const lockedIsMostRecent =
    isLocked &&
    !!mostRecentRevision &&
    lockedRevision!.id === mostRecentRevision.id;

  const mostRecentHasErrors =
    mostRecentRevision != null && mostRecentRevision.totalErrors > 0;

  const lockDisabled = isCertified || isCycleEnded || mostRecentHasErrors;
  const lockDisabledReason = isCertified
    ? "Cannot lock — cycle is certified"
    : isCycleEnded
      ? "Cannot lock — amendment window has closed"
      : mostRecentHasErrors
        ? `Resolve ${mostRecentRevision!.totalErrors} fatal error${mostRecentRevision!.totalErrors === 1 ? "" : "s"} before locking`
        : undefined;

  const unlockDisabled = isCertified || isCycleEnded;
  const unlockDisabledReason = isCertified
    ? "Cannot unlock — cycle is certified"
    : isCycleEnded
      ? "Cannot unlock — amendment window has closed"
      : undefined;

  const getReviewerName = (): string => {
    const accounts = globalState.pca.getAllAccounts();
    return accounts.length > 0 && accounts[0].name ? accounts[0].name : "";
  };

  const postCertificationLog = async (
    log: Partial<CertificationLog>
  ): Promise<void> => {
    try {
      await fetchAuthenticatedData<CertificationLog>(
        globalState,
        `/json/certificationlogs/${esiidPath}/add`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify(log),
        }
      );
      queryClient.invalidateQueries({
        queryKey: ["certification_logs", esiidPath, certificationId],
      });
    } catch (err) {
      console.error("[useRevisions] Failed to post certification log:", err);
    }
  };

  const invalidateQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["certifications", esiidPath] });
    queryClient.invalidateQueries({
      queryKey: ["certification_stats", esiidPath, certificationId],
    });
    queryClient.invalidateQueries({
      queryKey: ["certification_logs", esiidPath, certificationId],
    });
  }, [queryClient, esiid, esiidPath, certificationId]);

  const lockMutation = useMutation({
    mutationFn: async (anomalyRunId: string) => {
      const cert = await fetchAuthenticatedData<Certification>(
        globalState,
        `/json/certification/${esiidPath}/${certificationId}`
      );

      cert.AnomalyRunId = parseInt(anomalyRunId, 10);

      const accounts = globalState.pca.getAllAccounts();
      if (accounts.length > 0 && accounts[0].name) {
        cert.LastReviewer = accounts[0].name;
      }

      await postCertificationEdit(globalState, esiidPath, cert);

      return anomalyRunId;
    },
    onSuccess: (anomalyRunId) => {
      setLockedRevisionId(anomalyRunId);
      invalidateQueries();
      setToast({
        title: "Locked",
        message: "Revision locked successfully",
        variant: "lock",
      });

      postCertificationLog({
        CertificationId: Number(certificationId),
        LockAnomalyRunId: Number(anomalyRunId),
        Reviewer: getReviewerName(),
        ReviewerDate: new Date().toISOString(),
      });
    },
    onError: (error) => {
      console.error("Failed to lock revision:", error);
      setToast({
        title: "Error",
        message: "Failed to lock revision. Please try again.",
        variant: "lock",
      });
    },
  });

  const unlockMutation = useMutation({
    mutationFn: async () => {
      const unlockedRunId = lockedRevisionId;

      const cert = await fetchAuthenticatedData<Certification>(
        globalState,
        `/json/certification/${esiidPath}/${certificationId}`
      );

      cert.AnomalyRunId = null;

      const accounts = globalState.pca.getAllAccounts();
      if (accounts.length > 0 && accounts[0].name) {
        cert.LastReviewer = accounts[0].name;
      }

      await postCertificationEdit(globalState, esiidPath, cert);

      return unlockedRunId;
    },
    onSuccess: (unlockedRunId) => {
      setLockedRevisionId(null);
      invalidateQueries();
      setToast({
        title: "Unlocked",
        message: "Revision unlocked successfully",
        variant: "unlock",
      });

      postCertificationLog({
        CertificationId: Number(certificationId),
        UnLockAnomalyRunId: unlockedRunId ? Number(unlockedRunId) : null,
        Reviewer: getReviewerName(),
        ReviewerDate: new Date().toISOString(),
      });
    },
    onError: (error) => {
      console.error("Failed to unlock revision:", error);
      setToast({
        title: "Error",
        message: "Failed to unlock revision. Please try again.",
        variant: "unlock",
      });
    },
  });

  // ── Handlers ───────────────────────────────────────────────

  const handleLock = (revisionId: string) => {
    if (lockMutation.isPending) return;
    lockMutation.mutate(revisionId);
  };

  const handleUnlock = (_revisionId: string) => {
    if (unlockMutation.isPending) return;
    unlockMutation.mutate();
  };

  const handleReplaceLock = (newRevisionId: string) => {
    if (!lockedRevisionId || lockMutation.isPending) return;
    lockMutation.mutate(newRevisionId);
  };

  const clearToast = () => setToast(null);

  return {
    lockedRevision,
    lockedRevisionId,
    isCertified,
    lockedIsMostRecent,
    mostRecentHasErrors,
    lockDisabled,
    lockDisabledReason,
    unlockDisabled,
    unlockDisabledReason,
    toast,
    clearToast,
    handleLock,
    handleUnlock,
    handleReplaceLock,
    isLocking: lockMutation.isPending,
    isUnlocking: unlockMutation.isPending,
    lockError: lockMutation.error,
    unlockError: unlockMutation.error,
  };
}
